import { useCallback, useEffect } from 'react';

import { BankAccountsModel, OutletModel, ProfileModel } from '@pukka/api';
import { hookUtils } from '@pukka/common/utils';
import { Button, InformationBox, Modal, Stepper } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { DetailForm } from './DetailForm';
import { GeneralForm } from './GeneralForm';
import { DerivedState, GeneralFormState, GopayActivationFormState, onPreviousStep, onSubmitForm } from './GopayActivationForm.state';
import { TnCForm } from './TnCForm';
import { UploadForm } from './UploadForm';

interface GopayActivationFormProps {
  readonly open?: boolean;
  readonly profileData: ProfileModel.Response;
  readonly outlets: OutletModel.Outlet[];
  readonly bankList: BankAccountsModel.BankList;
  readonly onOpenChange?: (open: boolean) => void;
  readonly Derived?: typeof DerivedState;
}

const STEPS: string[] = ['Data Umum', 'Kelengkapan Data', 'Dokumentasi', 'Persyaratan'];

function GopayActivationForm({
  open = false,
  profileData,
  outlets,
  bankList,
  Derived = DerivedState,
  onOpenChange, // onSubmitSuccess,
}: GopayActivationFormProps) {
  const { step, isLoading, errors } = useSnapshot(GopayActivationFormState);
  const { businessType } = useSnapshot(GeneralFormState);
  const { isComplete } = useSnapshot(Derived);
  const { ref, scrollToTop } = hookUtils.useScrollToTop<HTMLDivElement>();

  const handleCloseModal = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const onSubmitFormHandler = useCallback(() => {
    onSubmitForm(profileData, outlets, handleCloseModal);
  }, [profileData, outlets, handleCloseModal]);

  useEffect(() => {
    if (open) {
      scrollToTop();
    }
  }, [step, scrollToTop, open]);

  return (
    <Modal unwrap open={open} onOpenChange={onOpenChange}>
      <Modal.Content className="max-w-2xl bg-neutral-100" onInteractOutside={(e) => e.preventDefault()}>
        <Modal.Title>
          QRIS Registration Form
          {!isLoading && (
            <Button icon="close" variant="nude" size="none" className="ml-auto rounded-md text-white" onClick={handleCloseModal} />
          )}
        </Modal.Title>
        <Modal.Header>
          <Stepper index={step} steps={STEPS} className="mt-2" />
        </Modal.Header>
        <Modal.Body className="min-h-[500px]">
          <div className="rounded-md bg-white p-4 view-transition-[content]" ref={ref}>
            {errors?.length > 0 && (
              <InformationBox variant="danger" className="mb-4" title="Please check your submission">
                <ul className="list-disc pl-4">
                  {errors.map((error) => (
                    <li>{error}</li>
                  ))}
                </ul>
              </InformationBox>
            )}
            {step === 0 && <GeneralForm outlets={outlets} bankList={bankList} />}
            {step === 1 && <DetailForm businessType={businessType!} />}
            {step === 2 && <UploadForm businessType={businessType!} />}
            {step === 3 && <TnCForm />}
          </div>
        </Modal.Body>
        <Modal.Footer className="relative -z-10 flex-row">
          <Modal.Button variant="secondary" onClick={onPreviousStep} disabled={step === 0 || isLoading} className="w-24">
            Back
          </Modal.Button>
          <Modal.Button loading={isLoading} disabled={step === 3 && !isComplete} onClick={onSubmitFormHandler} className="ml-auto w-24">
            {step === 3 ? 'Submit' : 'Next'}
          </Modal.Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export { GopayActivationForm };
